// The Work page's rules, free of React so they test (model.test.ts): what a
// scope shows, filters and grouping, the List's tree, what a drag between
// swimlanes changes, the timesheet's rows, and dates.
import type { CommentThreadEntry } from '@olytic/foundation';

import type {
  TimeEntry, WorkBootstrap, WorkComment, WorkItem, WorkItemPatch, WorkPerson, WorkPriority, WorkProject, WorkStatus, WorkType,
} from './workApi';

/** Maps the server's comments to Foundation's thread entries without fetching or mutating data. */
export function commentThreadEntries(
  comments: readonly WorkComment[],
  avatarUrlForPerson?: (personId: string) => string | undefined,
): CommentThreadEntry[] {
  return comments.map((comment) => {
    const editable = comment.kind === 'comment' && comment.mine;
    let author: CommentThreadEntry['author'] = null;
    if (comment.kind === 'comment' && comment.author?.type === 'person') {
      const avatarUrl = avatarUrlForPerson?.(comment.author.id);
      author = { name: comment.author.name, ...(avatarUrl ? { avatarUrl } : {}) };
    } else if (comment.kind === 'comment' && comment.author?.type === 'agent') {
      author = { name: comment.author.name, badge: 'Agent' };
    }
    return {
      id: comment.id,
      kind: comment.kind,
      author,
      body: comment.body,
      at: comment.created_at,
      edited: comment.edited_at !== null,
      canEdit: editable,
      canDelete: editable,
    };
  });
}

export const STATUSES: readonly { key: WorkStatus; label: string; tone: 'neutral' | 'accent' | 'warning' | 'success' }[] = [
  { key: 'backlog', label: 'Backlog', tone: 'neutral' },
  { key: 'to_do', label: 'To Do', tone: 'neutral' },
  { key: 'in_progress', label: 'In Progress', tone: 'accent' },
  { key: 'in_review', label: 'In Review', tone: 'warning' },
  { key: 'done', label: 'Done', tone: 'success' },
];
export const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label])) as Record<WorkStatus, string>;

export const PRIORITIES: readonly [WorkPriority, string][] = [
  ['highest', 'Highest'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low'], ['lowest', 'Lowest'],
];
export const PRIORITY_LABEL = Object.fromEntries(PRIORITIES) as Record<WorkPriority, string>;

export const TYPES: readonly [WorkType, string][] = [
  ['epic', 'Epic'], ['feature', 'Feature'], ['task', 'Task'], ['bug', 'Bug'], ['subtask', 'Subtask'],
];
export const TYPE_LABEL = Object.fromEntries(TYPES) as Record<WorkType, string>;

export const LINK_LABEL = {
  blocks: ['blocks', 'is blocked by'],
  relates: ['relates to', 'relates to'],
  duplicates: ['duplicates', 'is duplicated by'],
} as const;

export const PROJECT_STATUS_LABEL: Record<string, string> = {
  planned: 'Planned', active: 'Active', on_hold: 'On hold', done: 'Done', cancelled: 'Cancelled',
};

export type Scope = { kind: 'project'; projectId: string } | { kind: 'mine' } | { kind: 'all' };
export const isCross = (scope: Scope) => scope.kind !== 'project';

/** The Work page's tabs, in order (WorkPage.tsx draws them). */
export type View = 'list' | 'board' | 'timesheet' | 'related' | 'files' | 'team' | 'feedback';
/** Tabs a project has and the cross-project views (My Work, All Work) don't. */
export const PROJECT_ONLY_VIEWS: readonly View[] = ['related', 'files', 'team', 'feedback'];
/** Tabs only an Admin sees: client feedback holds the names and emails of a client's users (MOTHER-327). */
export const ADMIN_ONLY_VIEWS: readonly View[] = ['feedback'];

/** Whether a tab shows: project-only tabs not across projects, Admin-only tabs to Admins only. */
export const viewShown = (view: View, { cross, admin }: { cross: boolean; admin: boolean }) =>
  !(cross && PROJECT_ONLY_VIEWS.includes(view)) && (admin || !ADMIN_ONLY_VIEWS.includes(view));

/** The tab a request lands on: the one asked for if it shows here, else List. */
export const shownView = (requested: View, where: { cross: boolean; admin: boolean }): View =>
  viewShown(requested, where) ? requested : 'list';

/** The first `max` characters (code points) of a message, its whitespace runs one space, with … when cut. */
export function excerpt(text: string, max = 140): string {
  const chars = [...text.replace(/\s+/g, ' ').trim()];
  return chars.length <= max ? chars.join('') : `${chars.slice(0, max).join('').trimEnd()}…`;
}

export type FacetKey = 'project' | 'epic' | 'assignee' | 'status' | 'type' | 'priority' | 'labels';
export const FACETS: readonly [FacetKey, string][] = [
  ['project', 'Project'], ['epic', 'Epic'], ['assignee', 'Assignee'], ['status', 'Status'],
  ['type', 'Work type'], ['priority', 'Priority'], ['labels', 'Labels'],
];
export type Facets = Record<FacetKey, string[]>;
export const NO_FACETS: Facets = { project: [], epic: [], assignee: [], status: [], type: [], priority: [], labels: [] };

export type GroupKey = 'none' | 'project' | 'assignee' | 'epic' | 'labels' | 'priority' | 'type';
export const GROUPS: readonly [GroupKey, string][] = [
  ['none', 'None'], ['project', 'Project'], ['assignee', 'Assignee'], ['epic', 'Epic'],
  ['labels', 'Labels'], ['priority', 'Priority'], ['type', 'Work type'],
];

/** "Nothing" for a facet or group: no epic, unassigned, no label. */
export const NONE = '_none';

/**
 * An assignee as one value: a team member's id, or "contact:" and a contact's
 * id, so pickers, facets and swimlanes treat both kinds alike.
 */
export const CONTACT = 'contact:';
export const assigneeValue = (item: WorkItem): string | null =>
  item.assignee_id ?? (item.assignee_contact_id ? `${CONTACT}${item.assignee_contact_id}` : null);
export const assigneePatch = (value: string | null): WorkItemPatch =>
  value?.startsWith(CONTACT) ? { assignee_contact_id: value.slice(CONTACT.length) } : { assignee_id: value || null };

/**
 * Who can be assigned on a project: its team, then the contacts at its account, named with the account.
 * An item's current contact stays in the list even when it isn't offered (a team-only person is offered none).
 * When there are contacts, both runs carry a group ("Team", "Contacts"), which OptionSelect shows as headings.
 */
export function assigneeOptions(boot: WorkBootstrap, projectId: string, current?: WorkItem): { value: string; label: string; group?: string }[] {
  const project = boot.projects.find((p) => p.id === projectId);
  const team = boot.members
    .filter((m) => m.project_id === projectId)
    .map((m) => ({ value: m.app_user_id, label: boot.people.find((p) => p.id === m.app_user_id)?.name ?? 'Someone' }));
  const contacts = project ? boot.contacts
    .filter((c) => c.account_id === project.account_id)
    .map((c) => ({ value: `${CONTACT}${c.id}`, label: `${c.name} (${project.account_name})` })) : [];
  const assigned = current?.assignee_contact;
  if (assigned && !contacts.some((c) => c.value === `${CONTACT}${assigned.id}`)) {
    contacts.push({ value: `${CONTACT}${assigned.id}`, label: project ? `${assigned.name} (${project.account_name})` : assigned.name });
  }
  if (contacts.length === 0) return team;
  return [...team.map((o) => ({ ...o, group: 'Team' })), ...contacts.map((o) => ({ ...o, group: 'Contacts' }))];
}

export interface Filters {
  q: string;
  facets: Facets;
  hideDone: boolean;
  showArchived: boolean;
}

export const anyFacet = (facets: Facets) => FACETS.some(([f]) => facets[f].length > 0);
export const filtersActive = (f: Filters) => f.q.trim() !== '' || anyFacet(f.facets);

/** The epic an item rolls up to: itself when it is one. */
export const epicOf = (item: WorkItem) => (item.type === 'epic' ? item.id : item.epic_id);

/** An item's values for a facet or a grouping. */
export function fieldValues(item: WorkItem, field: FacetKey): string[] {
  switch (field) {
    case 'project': return [item.project_id];
    case 'epic': return [epicOf(item) ?? NONE];
    case 'assignee': return [assigneeValue(item) ?? NONE];
    case 'labels': return item.labels.length ? item.labels : [NONE];
    case 'status': return [item.status];
    case 'type': return [item.type];
    case 'priority': return [item.priority];
  }
}

export const isBlocked = (item: WorkItem) => item.status !== 'done' && (item.is_blocked || item.blocked_by.length > 0);

/** Whether an item passes the search, the facets and the archived/done switches. */
export function matches(item: WorkItem, f: Filters, { list }: { list: boolean }): boolean {
  if (item.archived_at && !f.showArchived) return false;
  if (list && f.hideDone && item.status === 'done') return false;
  const q = f.q.trim().toLowerCase();
  if (q && !(item.title.toLowerCase().includes(q) || item.key.toLowerCase().includes(q) || item.labels.some((l) => l.includes(q)))) return false;
  return FACETS.every(([field]) => {
    const chosen = f.facets[field];
    return chosen.length === 0 || fieldValues(item, field).some((v) => chosen.includes(v));
  });
}

/** Ranked within a project, projects in code order. */
export function byRank(projects: Map<string, WorkProject>) {
  return (a: WorkItem, b: WorkItem) => {
    if (a.project_id !== b.project_id) {
      return (projects.get(a.project_id)?.code ?? '').localeCompare(projects.get(b.project_id)?.code ?? '');
    }
    return a.board_rank - b.board_rank || a.number - b.number;
  };
}

export interface TreeRow {
  item: WorkItem;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
}

/**
 * The List's rows: epics, their items and subtasks as a tree, in rank order.
 * An item shows when it or anything beneath it matches; while filters are on,
 * every branch with a match is open.
 */
export function treeRows(items: WorkItem[], f: Filters, collapsed: ReadonlySet<string>, order: (a: WorkItem, b: WorkItem) => number): TreeRow[] {
  const ids = new Set(items.map((i) => i.id));
  const kids = new Map<string, WorkItem[]>();
  for (const i of items) {
    if (i.parent_id && ids.has(i.parent_id)) {
      if (!kids.has(i.parent_id)) kids.set(i.parent_id, []);
      kids.get(i.parent_id)!.push(i);
    }
  }
  for (const list of kids.values()) list.sort(order);
  const memo = new Map<string, boolean>();
  const shows = (i: WorkItem): boolean => {
    if (!memo.has(i.id)) memo.set(i.id, matches(i, f, { list: true }) || (kids.get(i.id) ?? []).some(shows));
    return memo.get(i.id)!;
  };
  const forceOpen = filtersActive(f);
  const out: TreeRow[] = [];
  const visit = (i: WorkItem, depth: number) => {
    if (!shows(i)) return;
    const children = (kids.get(i.id) ?? []).filter(shows);
    const expanded = children.length > 0 && (forceOpen || !collapsed.has(i.id));
    out.push({ item: i, depth, hasChildren: children.length > 0, expanded });
    if (expanded) for (const c of children) visit(c, depth + 1);
  };
  items.filter((i) => !i.parent_id || !ids.has(i.parent_id)).sort(order).forEach((i) => visit(i, 0));
  return out;
}

export interface Lookups {
  projects: Map<string, WorkProject>;
  people: Map<string, WorkPerson>;
  /** Epics in scope, by id, for their titles. */
  epics: Map<string, WorkItem>;
}

/** Facet options (or group headings) for the items in view, in a useful order. */
export function optionsFor(field: FacetKey, items: WorkItem[], look: Lookups): [string, string][] {
  switch (field) {
    case 'project': {
      const used = new Set(items.map((i) => i.project_id));
      return [...look.projects.values()].filter((p) => used.has(p.id)).sort((a, b) => a.code.localeCompare(b.code)).map((p) => [p.id, p.name]);
    }
    case 'epic': {
      const epics = new Map<string, string>();
      for (const i of items) {
        const e = epicOf(i);
        if (e) epics.set(e, look.epics.get(e)?.title ?? (i.type === 'epic' ? i.title : i.epic_title) ?? 'Epic');
      }
      const rank = (id: string) => look.epics.get(id)?.board_rank ?? 0;
      return [...[...epics.entries()].sort((a, b) => rank(a[0]) - rank(b[0])), [NONE, 'No epic']];
    }
    case 'assignee': {
      const used = new Set(items.map((i) => i.assignee_id).filter((v): v is string => v !== null));
      const contacts = new Map(items.flatMap((i) => (i.assignee_contact ? [[`${CONTACT}${i.assignee_contact.id}`, i.assignee_contact.name] as const] : [])));
      return [
        ...[...look.people.values()].filter((p) => used.has(p.id)).map((p): [string, string] => [p.id, p.name]),
        ...[...contacts.entries()].sort((a, b) => a[1].localeCompare(b[1])),
        [NONE, 'Unassigned'],
      ];
    }
    case 'status': return STATUSES.map((s) => [s.key, s.label]);
    case 'type': return TYPES.filter(([t]) => t !== 'epic').map(([k, l]) => [k, l]);
    case 'priority': return PRIORITIES.map(([k, l]) => [k, l]);
    case 'labels': return [...[...new Set(items.flatMap((i) => i.labels))].sort().map((l): [string, string] => [l, l]), [NONE, 'No label']];
  }
}

export interface Group {
  key: string;
  label: string;
  items: WorkItem[];
}

/** Items grouped by a field, groups in option order, empty groups left out. */
export function groupsFor(items: WorkItem[], field: Exclude<GroupKey, 'none'>, look: Lookups): Group[] {
  const shown = field === 'epic' ? items.filter((i) => i.type !== 'epic') : items;
  return optionsFor(field, items, look)
    .map(([key, label]) => ({ key, label, items: shown.filter((i) => fieldValues(i, field).includes(key)) }))
    .filter((g) => g.items.length > 0);
}

/**
 * What dropping an item into another swimlane changes, or a message saying
 * why it cannot move that way.
 */
export function groupMove(item: WorkItem, field: Exclude<GroupKey, 'none'>, from: string, to: string): { patch: WorkItemPatch; note: string } | { error: string } | null {
  if (from === to) return null;
  switch (field) {
    case 'project': return { error: 'Move a work item to another project from its own page.' };
    case 'assignee': return { patch: assigneePatch(to === NONE ? null : to), note: to === NONE ? 'unassigned' : 'assigned' };
    case 'epic':
      if (item.type === 'subtask') return { error: 'Subtasks follow their parent’s epic.' };
      return { patch: { parent_id: to === NONE ? null : to }, note: to === NONE ? 'removed from its epic' : 'moved to the epic' };
    case 'priority': return { patch: { priority: to as WorkPriority }, note: `${PRIORITY_LABEL[to as WorkPriority]} priority` };
    case 'type':
      if (item.type === 'subtask' || to === 'subtask') return { error: 'Change a subtask’s type from the work item itself.' };
      return { patch: { type: to as WorkType }, note: `now a ${TYPE_LABEL[to as WorkType].toLowerCase()}` };
    case 'labels': {
      const labels = item.labels.filter((l) => l !== from);
      if (to !== NONE && !labels.includes(to)) labels.push(to);
      return { patch: { labels }, note: to === NONE ? `label ${from} removed` : `labeled ${to}` };
    }
  }
}

/** Active team members of a project. */
export function teamOf(boot: WorkBootstrap, projectId: string): string[] {
  return boot.members.filter((m) => m.project_id === projectId).map((m) => m.app_user_id);
}

export const onTeam = (boot: WorkBootstrap, projectId: string, personId: string) =>
  boot.members.some((m) => m.project_id === projectId && m.app_user_id === personId);

// ── Dates ─────────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const parse = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`);
const isoOf = (d: Date) => d.toISOString().slice(0, 10);

export function todayIso(now = new Date()): string {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function addDays(iso: string, n: number): string {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return isoOf(d);
}

/** The first day of the week holding `iso`, weeks starting Sunday (0) or Monday (1). */
export function weekStart(iso: string, startsOn: 0 | 1): string {
  const dow = parse(iso).getUTCDay();
  return addDays(iso, -((dow - startsOn + 7) % 7));
}

export const shortDate = (iso: string | null) => (iso ? `${MONTHS[parse(iso).getUTCMonth()]} ${parse(iso).getUTCDate()}` : '');
export const dayLabel = (iso: string) => `${DAYS[parse(iso).getUTCDay()]} ${parse(iso).getUTCDate()}`;
export const isWeekend = (iso: string) => [0, 6].includes(parse(iso).getUTCDay());
export const monthOf = (iso: string) => iso.slice(0, 7);
export const monthLabel = (month: string) => `${FULL_MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

export function weekLabel(start: string): string {
  const end = addDays(start, 6);
  return `${shortDate(start)} – ${shortDate(end)}, ${end.slice(0, 4)}`;
}

export function monthRange(first: string | null, last: string | null): string {
  if (!first) return '';
  const name = (m: string) => MONTHS[Number(m.slice(5, 7)) - 1] ?? m;
  return first === last || !last ? name(first) : `${name(first)}–${name(last)}`;
}

/** Due today, overdue or neither, for an open item. */
export function dueState(item: WorkItem, today: string): 'overdue' | 'today' | null {
  if (!item.due_date || item.status === 'done') return null;
  if (item.due_date < today) return 'overdue';
  return item.due_date === today ? 'today' : null;
}

export const hoursText = (n: number) => String(Math.round(n * 100) / 100);

// ── Timesheet ─────────────────────────────────────────────────────────────

/** A timesheet row key: the project, and the work item or nothing (time on the project itself). */
export const rowKey = (projectId: string, itemId: string | null) => `${projectId}|${itemId ?? ''}`;
export function splitRowKey(key: string): { project_id: string; work_item_id: string | null } {
  const [project_id = '', item = ''] = key.split('|');
  return { project_id, work_item_id: item || null };
}

export interface SheetRow {
  key: string;
  project_id: string;
  work_item_id: string | null;
  hours: Record<string, number>;
  billable: boolean | 'mixed';
}

/** One row per project or work item with time this week, plus rows added by hand, in project then rank order. */
export function sheetRows(entries: TimeEntry[], extra: string[], order: (a: string, b: string) => number): SheetRow[] {
  const rows = new Map<string, SheetRow & { flags: Set<boolean> }>();
  const ensure = (key: string) => {
    if (!rows.has(key)) rows.set(key, { key, ...splitRowKey(key), hours: {}, billable: true, flags: new Set() });
    return rows.get(key)!;
  };
  for (const e of entries) {
    const row = ensure(rowKey(e.project_id, e.work_item_id));
    row.hours[e.entry_date] = Math.round(((row.hours[e.entry_date] ?? 0) + e.hours) * 100) / 100;
    row.flags.add(e.billable);
  }
  for (const key of extra) ensure(key);
  return [...rows.values()]
    .sort((a, b) => order(a.key, b.key))
    .map(({ flags, ...row }) => ({ ...row, billable: flags.size === 2 ? 'mixed' : flags.size === 1 ? [...flags][0]! : true }));
}

/** Where an edited time entry's hours move, in words, or null when they stay put. */
export function moveText(
  before: Pick<TimeEntry, 'entry_date' | 'owner_id' | 'project_id' | 'hours'>,
  after: { entry_date: string; owner_id: string; project_id: string },
  names: { person: (id: string) => string; project: (id: string) => string },
): string | null {
  const changes: string[] = [];
  if (monthOf(before.entry_date) !== monthOf(after.entry_date)) changes.push(`${monthLabel(monthOf(before.entry_date))} → ${monthLabel(monthOf(after.entry_date))}`);
  if (before.owner_id !== after.owner_id) changes.push(`${names.person(before.owner_id)} → ${names.person(after.owner_id)}`);
  if (before.project_id !== after.project_id) changes.push(`${names.project(before.project_id)} → ${names.project(after.project_id)}`);
  if (changes.length === 0) return null;
  return `Saving moves ${hoursText(before.hours)} h out of ${names.person(before.owner_id)}’s ${monthLabel(monthOf(before.entry_date))} on ${names.project(before.project_id)} and into the new period (${changes.join(', ')}). Resourcing totals update on save.`;
}
